import { ruteroBookingRecoveryMode } from '@bandido/shared/types';
import type {
  RuteroBookingRecoveryMode,
  RuteroBookingRecoveryState,
} from '@bandido/shared/types';

export type BookingRecoveryView = RuteroBookingRecoveryState & {
  recoveryMode?: RuteroBookingRecoveryMode;
};

export function bookingRecoveryMode(booking: BookingRecoveryView): RuteroBookingRecoveryMode {
  return booking.recoveryMode ?? ruteroBookingRecoveryMode(booking);
}

export function bookingRecoveryChipKey(booking: BookingRecoveryView): string | undefined {
  const mode = bookingRecoveryMode(booking);
  if (mode === 'manual') return 'travelServices.booking.manualChip';
  if (mode === 'same_attempt_retry') {
    return 'travelServices.booking.sameAttemptRetryChip';
  }
  if (mode === 'sync') return 'travelServices.booking.pendingChip';
  return undefined;
}

export function bookingRecoveryNoticeKey(booking: BookingRecoveryView): string | undefined {
  const mode = bookingRecoveryMode(booking);
  if (mode === 'manual') return 'travelServices.booking.manualNotice';
  if (mode === 'same_attempt_retry') {
    return 'travelServices.booking.sameAttemptRetryNotice';
  }
  if (mode === 'sync') return 'travelServices.booking.pendingVerification';
  return undefined;
}

export function canSyncBookingRecovery(booking: BookingRecoveryView): boolean {
  return bookingRecoveryMode(booking) === 'sync';
}

/**
 * El código con el que el backend rechaza un hold cuyo TRANSPORTE quedó
 * ambiguo (timeout, conexión cortada, respuesta no parseable). No es un fallo
 * del proveedor: la reserva pudo haberse creado igual, y el reintento con la
 * MISMA `Idempotency-Key` es la única acción segura (§5 de docs/RUTERO.md).
 */
export const RUTERO_HOLD_TRANSPORT_AMBIGUOUS = 'RUTERO_HOLD_TRANSPORT_AMBIGUOUS';

/**
 * Las respuestas de hold que se pintan en ROJO porque requieren una salida
 * explícita del asesor, normalmente volver a buscar.
 *
 * Incluye rechazos limpios, pero no coincide con el conjunto clean-terminal:
 * `shouldResetHoldAttemptAfterError` (`holdAttemptIdentity.ts`) libera otros
 * códigos que la pantalla trata mejor por su cuenta. También incluye Rail
 * `provider_hold_expires`, que NO es limpio: conserva el intento y bloquea
 * reintentos mientras vive el hold remoto temporal.
 */
export const TERMINAL_HOLD_ERROR_CODES = [
  'RUTERO_PROVENANCE_EXPIRED',
  'RUTERO_OFFER_EXPIRED',
  'RUTERO_OFFER_SELECTION_CHANGED',
  'RUTERO_HOLD_RAIL_PASSENGER_COMPOSITION_MISMATCH',
  'RUTERO_HOLD_RAIL_PLACE_PROPERTY_REQUIRED',
  'RUTERO_PROVIDER_POLICY',
  'RUTERO_HOLD_CLEAN_FAILED',
] as const;

export type TerminalHoldErrorCode = (typeof TERMINAL_HOLD_ERROR_CODES)[number];

/**
 * Las CUATRO caras de la recuperación. El backend ya deriva cuatro clases
 * excluyentes en `recoveryMode`, pero `sync` cubre DOS situaciones que el
 * asesor vive distinto —no sabemos si el proveedor completó la operación,
 * o la reserva está creada y falta cerrar el registro comercial—, así que la
 * cara se resuelve con el estado del intento además del modo.
 */
export type BookingRecoveryCardKind =
  | 'transportAmbiguous'
  | 'unknownStateSync'
  | 'manual'
  | 'adoptionPending';

/** Qué hace EL botón de la tarjeta. Uno solo por tarjeta. */
export type BookingRecoveryActionKind = 'retry' | 'sync' | 'manual';

export interface BookingRecoveryCardSpec {
  kind: BookingRecoveryCardKind;
  /** La clase del backend de la que sale esta cara (para el código de soporte). */
  recoveryMode: RuteroBookingRecoveryMode;
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  action: BookingRecoveryActionKind;
  /**
   * Cuánto pesa el botón. `adoptionPending` se cierra solo: su acción es un
   * empujón opcional y no puede competir con la decisión del momento.
   */
  emphasis: 'primary' | 'secondary';
}

const cardCopy = (kind: BookingRecoveryCardKind, part: string) =>
  `travelServices.booking.recovery.${kind}.${part}`;

export const BOOKING_RECOVERY_CARDS: Record<
  BookingRecoveryCardKind,
  BookingRecoveryCardSpec
> = {
  transportAmbiguous: {
    kind: 'transportAmbiguous',
    recoveryMode: 'same_attempt_retry',
    titleKey: cardCopy('transportAmbiguous', 'title'),
    bodyKey: cardCopy('transportAmbiguous', 'body'),
    ctaKey: cardCopy('transportAmbiguous', 'cta'),
    action: 'retry',
    emphasis: 'primary',
  },
  unknownStateSync: {
    kind: 'unknownStateSync',
    recoveryMode: 'sync',
    titleKey: cardCopy('unknownStateSync', 'title'),
    bodyKey: cardCopy('unknownStateSync', 'body'),
    ctaKey: cardCopy('unknownStateSync', 'cta'),
    action: 'sync',
    emphasis: 'primary',
  },
  manual: {
    kind: 'manual',
    recoveryMode: 'manual',
    titleKey: cardCopy('manual', 'title'),
    bodyKey: cardCopy('manual', 'body'),
    ctaKey: cardCopy('manual', 'cta'),
    action: 'manual',
    emphasis: 'primary',
  },
  adoptionPending: {
    kind: 'adoptionPending',
    recoveryMode: 'sync',
    titleKey: cardCopy('adoptionPending', 'title'),
    bodyKey: cardCopy('adoptionPending', 'body'),
    ctaKey: cardCopy('adoptionPending', 'cta'),
    action: 'sync',
    emphasis: 'secondary',
  },
};

export interface TerminalHoldErrorSpec {
  code: TerminalHoldErrorCode;
  titleKey: string;
  bodyKey?: string;
  /**
   * El mensaje que trae el rechazo lo escribió el PROVEEDOR (o el operador), y
   * por eso se muestra tal cual: es lo único accionable.
   *
   * En falso el texto lo genera client-api —una frase fija, en un solo
   * idioma—, así que no se muestra: la pantalla es bilingüe y el rótulo
   * traducido de la tarjeta dice exactamente lo mismo (§21.15: el backend no
   * manda strings de display).
   */
  usesProviderMessage: boolean;
  /**
   * Reenviar el pedido TAL CUAL no puede prosperar, pero la OFERTA sigue viva:
   * el camino es corregir lo pedido y volver a reservar.
   *
   * Es lo que dice el contrato del hub para `provider_policy` (409): "no es una
   * falla, trae `details.retryable: false` y reintentar no cambia nada; hay que
   * CORREGIR LO PEDIDO" — y su ejemplo canónico es corregible desde este mismo
   * modal (el operador de ferry rechaza la selección de acomodaciones porque no
   * cubre a todos los pasajeros). Vale igual para el rechazo limpio genérico,
   * cuyo mensaje viaja tal cual justamente porque nombra qué corregir
   * ("[transferz] exige teléfono del pasajero de contacto", §1 de
   * docs/RUTERO.md).
   *
   * Por eso el bloqueo es del PEDIDO y no de la oferta: el formulario se queda,
   * el CTA se deshabilita con el motivo del operador al lado, y en cuanto el
   * pedido cambia vuelve a habilitarse — con identidad de intento nueva, que es
   * lo correcto cuando el rechazo fue limpio. Rail es la excepción durable:
   * conserva el intento y el hold remoto hasta el vencimiento contractual.
   */
  blocksUnchangedRequest: boolean;
  /** Qué se puede hacer además de volver a buscar. */
  hintKey?: string;
  /**
   * La salida natural de este rechazo es RE-COTIZAR el mismo pedido en el
   * momento (una búsqueda idéntica con generación nueva), sin cerrar el modal
   * ni perder lo cargado. La tarjeta ofrece ese CTA cuando el buscador sabe
   * re-cotizar (hoy, transfers vía `onRequote`); sin ese canal queda solo
   * "buscar de nuevo".
   */
  offersRequote?: boolean;
}

export const TERMINAL_HOLD_ERRORS: Record<
  TerminalHoldErrorCode,
  TerminalHoldErrorSpec
> = {
  RUTERO_PROVENANCE_EXPIRED: {
    code: 'RUTERO_PROVENANCE_EXPIRED',
    titleKey: 'travelServices.booking.recovery.terminal.provenanceExpired',
    // client-api lo genera ('La oferta expiró: hay que volver a buscar'): el
    // proveedor no dijo nada acá.
    usesProviderMessage: false,
    // Acá no hay pedido que corregir: la oferta murió y el camino es volver a
    // buscar. El CTA ya desaparece por `requiresFreshSearch`.
    blocksUnchangedRequest: false,
  },
  RUTERO_OFFER_SELECTION_CHANGED: {
    code: 'RUTERO_OFFER_SELECTION_CHANGED',
    titleKey: 'travelServices.booking.recovery.terminal.selectionChanged',
    usesProviderMessage: true,
    blocksUnchangedRequest: false,
  },
  RUTERO_OFFER_EXPIRED: {
    code: 'RUTERO_OFFER_EXPIRED',
    titleKey: 'travelServices.booking.recovery.terminal.offerExpired',
    // client-api lo genera (frase fija): el proveedor no dijo nada acá — lo
    // que firmó fue el vencimiento, y eso lo dice el rótulo traducido.
    usesProviderMessage: false,
    // Como en provenance vencido: no hay pedido que corregir — la oferta
    // murió y el CTA de reservar ya desaparece por `requiresFreshSearch`.
    blocksUnchangedRequest: false,
    hintKey: 'travelServices.booking.recovery.terminal.offerExpiredHint',
    // A diferencia de provenance vencido, acá el corte lo firmó el proveedor
    // sobre ESTA cotización: re-cotizar el mismo pedido produce una oferta
    // vigente sin perder los pasajeros cargados.
    offersRequote: true,
  },
  RUTERO_HOLD_RAIL_PASSENGER_COMPOSITION_MISMATCH: {
    code: 'RUTERO_HOLD_RAIL_PASSENGER_COMPOSITION_MISMATCH',
    titleKey:
      'travelServices.booking.recovery.terminal.railPassengerCompositionMismatch',
    usesProviderMessage: false,
    blocksUnchangedRequest: false,
  },
  RUTERO_HOLD_RAIL_PLACE_PROPERTY_REQUIRED: {
    code: 'RUTERO_HOLD_RAIL_PLACE_PROPERTY_REQUIRED',
    titleKey: 'travelServices.booking.recovery.terminal.railPlacePropertyRequired',
    bodyKey: 'travelServices.booking.recovery.terminal.railPlacePropertyRequiredBody',
    usesProviderMessage: false,
    blocksUnchangedRequest: true,
  },
  RUTERO_PROVIDER_POLICY: {
    code: 'RUTERO_PROVIDER_POLICY',
    titleKey: 'travelServices.booking.recovery.terminal.providerPolicy',
    usesProviderMessage: true,
    blocksUnchangedRequest: true,
    hintKey: 'travelServices.booking.recovery.terminal.correctRequest',
  },
  RUTERO_HOLD_CLEAN_FAILED: {
    code: 'RUTERO_HOLD_CLEAN_FAILED',
    titleKey: 'travelServices.booking.recovery.terminal.cleanFailed',
    usesProviderMessage: true,
    blocksUnchangedRequest: true,
    hintKey: 'travelServices.booking.recovery.terminal.correctRequest',
  },
};

export function isTerminalHoldErrorCode(
  code: string | undefined,
): code is TerminalHoldErrorCode {
  return TERMINAL_HOLD_ERROR_CODES.includes(code as TerminalHoldErrorCode);
}

/**
 * ¿Esta tarjeta terminal YA dice que la oferta murió? Es exactamente el caso
 * `blocksUnchangedRequest: false`: no hay pedido que corregir porque la oferta
 * entera dejó de ser reservable (provenance vencido, vencimiento firmado). El
 * aviso genérico de "oferta vencida" del modal se calla SOLO ante estas
 * tarjetas — los rechazos que bloquean el PEDIDO no vencen la oferta, y si la
 * tarifa vence después el aviso tiene que seguir apareciendo (§21.9: una
 * definición, no dos condiciones parecidas en el JSX).
 */
export function terminalCardDeclaresOfferDead(
  spec: TerminalHoldErrorSpec,
): boolean {
  return !spec.blocksUnchangedRequest;
}

/**
 * Lo que la pantalla dibuja cuando algo sale raro. Los dos casos son
 * EXCLUYENTES por construcción: un estado recuperable produce `recovery`
 * (ámbar, con su acción segura) y nunca puede caer en `terminal` (rojo, sin
 * vuelta) — que es la invariante entera de esta pantalla.
 */
export interface BookingRecoveryCardSurface {
  kind: 'recovery';
  spec: BookingRecoveryCardSpec;
  /** El estado crudo, para soporte: `provider_unknown_state → sync`. */
  technicalCode: string;
}

export interface HoldTerminalErrorSurface {
  kind: 'terminal';
  spec: TerminalHoldErrorSpec;
  /** El motivo TAL CUAL lo dio el operador. Sin él queda el texto genérico. */
  message?: string;
  /** Vencimiento del hold temporal Rail; nunca es vigencia de la oferta. */
  providerHoldExpiresAt?: string;
}

export type BookingRecoverySurface =
  | BookingRecoveryCardSurface
  | HoldTerminalErrorSurface;

/** `provider_unknown_state → sync`: lo que soporte necesita leer del estado. */
export function bookingRecoveryTechnicalCode(booking: BookingRecoveryView): string {
  return `${booking.holdAttemptState ?? 'unknown'} → ${bookingRecoveryMode(booking)}`;
}

/**
 * De qué cara es este estado.
 *
 * `sync` es la única clase del backend que cubre dos situaciones distintas
 * para el asesor. Se separan por el estado del intento: `adoption_pending` sin
 * incertidumbre es "reservada, cerrando números"; todo lo demás (incertidumbre
 * declarada, o adopción pendiente CON incertidumbre) es "pendiente de
 * conciliación", que es lo más fuerte de los dos y no puede quedar tapado
 * por el más tranquilo.
 */
export function bookingRecoveryCardKind(
  booking: BookingRecoveryView,
): BookingRecoveryCardKind | null {
  const mode = bookingRecoveryMode(booking);
  if (mode === 'same_attempt_retry') return 'transportAmbiguous';
  if (mode === 'manual') return 'manual';
  if (mode !== 'sync') return null;
  return booking.holdAttemptState === 'adoption_pending' &&
    booking.unknownProviderState !== true
    ? 'adoptionPending'
    : 'unknownStateSync';
}

/**
 * La cara del estado de una reserva que YA existe (la fila es la autoridad).
 *
 * El tipo de retorno es la mitad recuperable a propósito: una fila viva NUNCA
 * puede producir el terminal rojo, y que no compile es mejor garantía que un
 * test.
 */
export function bookingRecoverySurface(
  booking: BookingRecoveryView,
): BookingRecoveryCardSurface | null {
  const kind = bookingRecoveryCardKind(booking);
  if (!kind) return null;
  return {
    kind: 'recovery',
    spec: BOOKING_RECOVERY_CARDS[kind],
    technicalCode: bookingRecoveryTechnicalCode(booking),
  };
}

/**
 * La cara del rechazo de un hold, cuando todavía NO hay fila que mostrar.
 *
 * Solo el reintento seguro puede dibujarse sin reserva: su acción es reenviar
 * el MISMO intento, que vive en el holder del modal. `sync` y `manual` operan
 * sobre una fila autoritativa — sin ella no hay nada que sincronizar ni que
 * conciliar, así que ese caso lo resuelve la lectura de la fila y no esta
 * función.
 */
export function holdErrorRecoverySurface({
  code,
  message,
  recoveryMode,
  providerHoldExpiresAt,
}: {
  code?: string;
  message?: string;
  recoveryMode?: RuteroBookingRecoveryMode;
  providerHoldExpiresAt?: string;
}): BookingRecoverySurface | null {
  if (
    code === RUTERO_HOLD_TRANSPORT_AMBIGUOUS ||
    recoveryMode === 'same_attempt_retry'
  ) {
    return {
      kind: 'recovery',
      spec: BOOKING_RECOVERY_CARDS.transportAmbiguous,
      technicalCode: `${code ?? RUTERO_HOLD_TRANSPORT_AMBIGUOUS} → same_attempt_retry`,
    };
  }
  if (isTerminalHoldErrorCode(code)) {
    const spec = TERMINAL_HOLD_ERRORS[code];
    return {
      kind: 'terminal',
      spec,
      ...(spec.usesProviderMessage && message?.trim()
        ? { message: message.trim() }
        : {}),
      ...(code === 'RUTERO_HOLD_RAIL_PLACE_PROPERTY_REQUIRED' &&
      typeof providerHoldExpiresAt === 'string' &&
      providerHoldExpiresAt.trim() !== '' &&
      Number.isFinite(Date.parse(providerHoldExpiresAt))
        ? { providerHoldExpiresAt }
        : {}),
    };
  }
  return null;
}

/**
 * Si el reintento seguro se puede DISPARAR desde donde se está mirando.
 *
 * El reenvío del mismo intento vive en el flujo de reserva y solo puede salir
 * mientras la oferta siga siendo reservable: la autoridad de frescura
 * pre-hold (`canStartPreHold`) es el guard final antes de cualquier mutación
 * de hold, y desde el detalle del listado directamente no hay envío que
 * repetir.
 *
 * Cuando no se puede, la tarjeta NO se queda sin decir nada ni con un botón
 * que no hace nada: el CTA va deshabilitado con el motivo al lado y nombrando
 * el camino que queda (verificar con el proveedor), que es lo único honesto
 * sobre una reserva que puede estar viva del otro lado.
 */
export function sameAttemptRetryAvailability({
  canResendHold,
}: {
  canResendHold: boolean;
}): { enabled: true } | { enabled: false; noteKey: string } {
  return canResendHold
    ? { enabled: true }
    : {
      enabled: false,
      noteKey: 'travelServices.booking.recovery.transportAmbiguous.noResend',
    };
}
